import { useCallback } from "react"
import { useAuth } from "@mimber/auth"
import { useCurrentOrganization } from "@mimber/client/mimber-organization"
import { checkActionHierarchy } from "../useActionHierarchy"
import { Resource, Action } from "@mimber/schema"
import type { UseOrganizationPermissionsReturn } from "./useOrganizationPermissions.types"

/**
 * Hook to fetch and manage organization user permissions
 * Fetches aggregated permissions from the current organization context
 */
export const useOrganizationPermissions =
  (): UseOrganizationPermissionsReturn => {
    const { user } = useAuth()
    const { data: currentOrg, isLoading, error } = useCurrentOrganization()

    const permissions = currentOrg?.data?.permissions ?? []

    const hasPermission = useCallback(
      (resource: Resource, action: Action) => {
        if (!user) return false

        // Find permissions for the specific resource
        const resourcePermissions = permissions.filter(
          (permission) => permission.resource === resource
        )

        // Find Resource.ALL permissions
        const allResourcePermissions = permissions.filter(
          (permission) => permission.resource === Resource.ALL
        )

        // If user has specific resource permissions, check if any satisfy the required action
        if (resourcePermissions.length > 0) {
          const hasSpecificPermission = resourcePermissions.some((permission) =>
            checkActionHierarchy(permission.action as Action, action)
          )

          if (hasSpecificPermission) {
            return true
          }

          // If specific resource exists but doesn't satisfy action,
          // check if Resource.ALL has higher permission
          if (allResourcePermissions.length > 0) {
            return allResourcePermissions.some((permission) =>
              checkActionHierarchy(permission.action as Action, action)
            )
          }

          return false
        }

        // No specific resource permissions, fall back to Resource.ALL
        return allResourcePermissions.some((permission) =>
          checkActionHierarchy(permission.action as Action, action)
        )
      },
      [permissions, user]
    )

    return {
      permissions,
      isLoading,
      error: error as Error | undefined,
      hasPermission,
    }
  }
